import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Checkbox, Button, CircularProgress, Alert, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions,
} from '@mui/material';
import { useApi } from './useApi';
import { usePermissions } from './PermissionsProvider';

// Features that are not permission-controlled, so every signed-in user has them. Keep in sync
// with the routes in App.js that have no RequirePermission guard.
const OPEN_TO_EVERYONE = [
  'New Cost Sheet', 'Dashboard (their own cost sheets)', 'Smart Search', 'Samples',
  'Sale & Purchase Invoices', 'Vendor Catalogs (browse)', 'Transportation, Customization & Marketing',
  'Client & Vendor management', 'Lead blocking (create & edit)',
];

/**
 * Role × permission grid. Each role column is edited independently and saved with its own
 * button, so one save never touches another role.
 */
const RolesManagement = () => {
  const api = useApi();
  const { refresh: refreshMyPermissions } = usePermissions();
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingRole, setSavingRole] = useState(null);
  const [confirmRole, setConfirmRole] = useState(null);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    Promise.all([api('/api/v2/rbac/roles'), api('/api/v2/rbac/permissions')])
      .then(([roleList, permissionList]) => {
        setRoles(roleList);
        setPermissions(permissionList);
        setDrafts(Object.fromEntries(roleList.map(r => [r.code, r.permissions])));
      })
      .catch(() => setMessage({ severity: 'error', text: 'Failed to load roles and permissions' }))
      .finally(() => setLoading(false));
  }, [api]);

  const toggle = (roleCode, permissionCode) => {
    setDrafts(prev => {
      const current = prev[roleCode] || [];
      return {
        ...prev,
        [roleCode]: current.includes(permissionCode)
          ? current.filter(p => p !== permissionCode)
          : [...current, permissionCode],
      };
    });
  };

  const isDirty = (role) => {
    const draft = drafts[role.code] || [];
    return draft.length !== role.permissions.length || draft.some(p => !role.permissions.includes(p));
  };

  const permissionLabel = code => permissions.find(p => p.code === code)?.description || code;

  const save = async (role) => {
    setConfirmRole(null);
    setSavingRole(role.code);
    setMessage(null);
    try {
      const saved = await api(`/api/v2/rbac/roles/${role.code}/permissions`, 'PUT', { permissions: drafts[role.code] || [] });
      setRoles(prev => prev.map(r => r.code === saved.code ? saved : r));
      setDrafts(prev => ({ ...prev, [saved.code]: saved.permissions }));
      setMessage({ severity: 'success', text: `${saved.name} updated` });
      refreshMyPermissions();
    } catch (e) {
      setMessage({
        severity: 'error',
        text: e.status === 409
          ? 'That change would remove your own Admin Tools access'
          : `Failed to update ${role.name}`,
      });
    } finally {
      setSavingRole(null);
    }
  };

  // The default role belongs to every user, so changes to it get an explicit confirmation.
  const requestSave = (role) => (role.isDefault ? setConfirmRole(role) : save(role));

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>;
  }

  const defaultRole = roles.find(r => r.isDefault);
  const added = confirmRole ? (drafts[confirmRole.code] || []).filter(p => !confirmRole.permissions.includes(p)) : [];
  const removed = confirmRole ? confirmRole.permissions.filter(p => !(drafts[confirmRole.code] || []).includes(p)) : [];

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          This grid lists only the <strong>restricted</strong> features. Everything else is open to every
          signed-in user, which is why the {defaultRole?.name || 'Employee'} column starts empty:
        </Typography>
        <Typography variant="body2" color="text.secondary">{OPEN_TO_EVERYONE.join(' · ')}</Typography>
      </Alert>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Tick a box to unlock that feature for everyone holding the role, then save that role.
        Changes apply to users within a minute.
      </Typography>
      {message && <Alert severity={message.severity} sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>}
      <TableContainer sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>Restricted feature</TableCell>
              {roles.map(r => (
                <TableCell key={r.code} align="center" sx={{ fontWeight: 700, bgcolor: '#f8fafc', whiteSpace: 'nowrap' }}>
                  <Tooltip title={r.isDefault ? 'Every signed-in user has this role' : (r.description || '')}>
                    <span>{r.name}{r.isDefault ? ' (everyone)' : ''}</span>
                  </Tooltip>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {permissions.map(p => (
              <TableRow key={p.code} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.8rem' }}>{p.description}</Typography>
                  <Typography variant="caption" color="text.secondary">{p.code}</Typography>
                </TableCell>
                {roles.map(r => (
                  <TableCell key={r.code} align="center" padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={(drafts[r.code] || []).includes(p.code)}
                      onChange={() => toggle(r.code, p.code)}
                      inputProps={{ 'aria-label': `${r.name}: ${p.code}` }}
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))}
            <TableRow>
              <TableCell />
              {roles.map(r => (
                <TableCell key={r.code} align="center">
                  <Button
                    size="small"
                    variant="contained"
                    disabled={!isDirty(r) || savingRole === r.code}
                    onClick={() => requestSave(r)}
                    sx={{ textTransform: 'none', fontWeight: 700 }}
                  >
                    {savingRole === r.code ? 'Saving…' : 'Save'}
                  </Button>
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={!!confirmRole} onClose={() => setConfirmRole(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Change access for every user?</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            {confirmRole?.name} is the default role, so this applies to <strong>everyone</strong> in the OMS.
            {added.length > 0 && <Box sx={{ mt: 1.5 }}>Everyone will be able to: <strong>{added.map(permissionLabel).join('; ')}</strong></Box>}
            {removed.length > 0 && <Box sx={{ mt: 1.5 }}>Everyone without another role will lose: <strong>{removed.map(permissionLabel).join('; ')}</strong></Box>}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setConfirmRole(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={() => save(confirmRole)} sx={{ textTransform: 'none', fontWeight: 700 }}>
            Apply to everyone
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RolesManagement;
